import { FC } from "react";
//#region types
import { SalaryFormTypes } from "models/formTypes";
import { WintSuperkollAPIModelCustomFilters } from "wint-api-client/dist/types";
import { ACTION_SEPARATOR } from "models/propTypes/menuPropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Grid } from "@mui/material";
import {
  ChartContextProvider,
  DashboardButtonGroup,
  FilterButton,
  PageLayouts,
  ReloadEvent,
  ReportSearchInput,
  SearchContextProvider,
  StyledButton,
  TitleWithMonthRangeNavigator,
  WidgetArea,
} from "components/common";
import { useSalaryPermissions } from "components/salary";
import { useEmployeeActions } from "components/employee";
import { useWintSalaryService } from "serviceHooks";
import SalaryReportList from "./_partials/salaryReportList";
import useSalaryReportFilterDefinition from "./useSalaryReportFilterDefinition";
import { useSalaryMonthPickerSelections } from "./useSalaryMonthPickerSelections";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import TotalSalaryChartWrapper from "./widgets/totalSalaryChartWrapper";
import GrossSalaryChartWrapper from "./widgets/grossSalaryChartWrapper";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
import useEarlyPayoutDialog from "./useEarlyPayoutDialog";
import useEarlyPayoutPermissions from "./useEarlyPayoutPermissions";

const WintSalaryReport: FC = () => {
  const { isWintSalaryDeviationsAvailable, isSalaryAdmin } = useSalaryPermissions();
  const { canCreateEarlyPayout } = useEarlyPayoutPermissions();

  const dispatch = useDispatch();
  const { getEarlyPayoutDialog } = useEarlyPayoutDialog();

  const { filterFormFileds, filterQueryStateHandler, apiToReportFilterFormatter, onClickClearAll } =
    useSalaryReportFilterDefinition();
  const { reportToApiFilter } = useWintSalaryService();
  const { employeeSettingsMoreAction, addEmployeeMoreAction } = useEmployeeActions();
  const monthPickerSelections = useSalaryMonthPickerSelections();
  const today = DateHelper.today();

  const initialFilter: SalaryFormTypes.WintSalaryReportFilter = {
    yearMonthRange: {
      start: DateHelper.getFirstDayOfYear(DateHelper.getYear(today)),
      end: DateHelper.getLastDayOfYear(DateHelper.getYear(today)),
    },
  };

  const title = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Salaries);

  const onCreateEarlyPayout = (onSubmitSuccess?: () => void) => {
    dispatch(displayDialog(getEarlyPayoutDialog(onSubmitSuccess)));
  };

  return (
    <ReloadEvent
      render={(reloadKey, reload) => (
        <SearchContextProvider>
          <PageLayouts.DashboardLayout<SalaryFormTypes.WintSalaryReportFilter>
            title={
              <TitleWithMonthRangeNavigator<SalaryFormTypes.WintSalaryReportFilter>
                title={title}
                monthPickerSelections={monthPickerSelections}
              />
            }
            entityKey={0}
            hasPermissions={isWintSalaryDeviationsAvailable}
            filterFields={filterFormFileds}
            filterCategory={WintSuperkollAPIModelCustomFilters.FilterCategory.WintSalaryReport}
            filterQueryStateHandler={filterQueryStateHandler}
            reportToAPIFilterFormatter={reportToApiFilter}
            apiToReportFilterFormatter={apiToReportFilterFormatter}
            initialFilter={initialFilter}
            onClickClearAll={onClickClearAll}
            topRightButtons={
              <DashboardButtonGroup
                buttons={[
                  <ReportSearchInput
                    placeholder={LocalizationHelperInstance.getFormattedMessage(
                      TranslationKeys.SalaryDeviation.SearchEmployee
                    )}
                  />,
                  ...(canCreateEarlyPayout
                    ? [
                        <StyledButton size="sm" onClick={onCreateEarlyPayout.bind(null, reload)}>
                          {LocalizationHelperInstance.getFormattedMessage(
                            TranslationKeys.Salary.ExtraPayoutCreateButtonText
                          )}
                        </StyledButton>,
                      ]
                    : []),
                  ACTION_SEPARATOR,
                  <FilterButton />,
                ]}
                moreActions={isSalaryAdmin ? [addEmployeeMoreAction, employeeSettingsMoreAction] : []}
              />
            }
          >
            <ChartContextProvider>
              <WidgetArea>
                <TotalSalaryChartWrapper />
                <GrossSalaryChartWrapper />
              </WidgetArea>
            </ChartContextProvider>
            <Grid container item>
              <SalaryReportList reloadKey={reloadKey} reloadList={reload} />
            </Grid>
          </PageLayouts.DashboardLayout>
        </SearchContextProvider>
      )}
    />
  );
};

export default WintSalaryReport;
